import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminMission, MissionTargetType } from '@/types/admin';

const TYPE_LABELS: Record<string, string> = {
  DAILY: 'Diária',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
  SPECIAL: 'Especial',
};

const TYPE_COLORS: Record<string, string> = {
  DAILY: 'text-green-400',
  WEEKLY: 'text-blue-400',
  MONTHLY: 'text-purple-400',
  SPECIAL: 'text-yellow-400',
};

const TARGET_TYPE_LABELS: Record<MissionTargetType, string> = {
  lessons_completed: 'Lições completadas',
  trails_completed: 'Trilhas completadas',
  xp_earned: 'XP acumulado',
  games_played: 'Jogos jogados',
  streak_days: 'Dias de streak',
  quiz_passed: 'Quizzes aprovados',
};

const AdminMissionsPage: FC = () => {
  const { showToast } = useToast();
  const [missions, setMissions] = useState<AdminMission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMissions();
  }, []);

  const loadMissions = async () => {
    try {
      const data = await adminService.getMissions();
      setMissions(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta missão?')) return;
    try {
      await adminService.deleteMission(id);
      await loadMissions();
    } catch {
      showToast('Erro ao excluir missão', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Missões</h1>
        <Link
          to="/admin/missoes/nova"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          Nova Missão
        </Link>
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : missions.length === 0 ? (
        <p className="text-content-muted">Nenhuma missão criada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="pb-3 font-medium">Missão</th>
                <th className="pb-3 font-medium text-center">Tipo</th>
                <th className="pb-3 font-medium">Meta</th>
                <th className="pb-3 font-medium text-center">XP</th>
                <th className="pb-3 font-medium text-center">Atribuídas</th>
                <th className="pb-3 font-medium text-center">Status</th>
                <th className="pb-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {missions.map((mission) => (
                <tr key={mission.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-2xl text-accent">
                        {mission.icon || 'flag'}
                      </span>
                      <div>
                        <p className="font-medium">{mission.title}</p>
                        <p className="text-content-muted text-sm truncate max-w-xs">{mission.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-center">
                    <span className={`text-sm font-medium ${TYPE_COLORS[mission.type] || ''}`}>
                      {TYPE_LABELS[mission.type] || mission.type}
                    </span>
                  </td>
                  <td className="py-4 text-sm text-content-secondary">
                    {TARGET_TYPE_LABELS[mission.target_type] || mission.target_type}: {mission.target_count}
                  </td>
                  <td className="py-4 text-center">{mission.xp_reward}</td>
                  <td className="py-4 text-center">{mission.assigned_count}</td>
                  <td className="py-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      mission.is_active
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {mission.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/admin/missoes/${mission.id}/editar`}
                        className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                        title="Editar"
                      >
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </Link>
                      <button
                        onClick={() => handleDelete(mission.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400 transition-colors"
                        title="Excluir"
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminMissionsPage;
